import axios from 'axios'
import { Capacitor } from "@capacitor/core";
import { currentTenant } from "../config/tenant";
import storageService from "../services/storageService";
import isExtranetHostname from '@/helpers/isExtranet'

const DEFAULT_API_HOST = window.location.origin;

class axiosHelper {
  constructor(method = "get", path, params = {}, data = null, options = {}) {
    this.path = path;
    this.params = params;
    this.data = data;
    this.method = method;

    // Determine custom headers from options, data, or params
    let extractedHeaders = {};

    if (options && typeof options === "object") {
      extractedHeaders = options.headers || options;
    }

    // Support caller passing { headers: ... } in data when data has no actual payload (e.g. POST/PUT with no body)
    if (
      this.data &&
      typeof this.data === "object" &&
      this.data.headers &&
      Object.keys(this.data).length === 1
    ) {
      extractedHeaders = { ...extractedHeaders, ...this.data.headers };
      this.data = null;
    }

    // Support caller passing { headers: ... } in params when params has no query parameters (e.g. GET/DELETE)
    if (
      this.params &&
      typeof this.params === "object" &&
      this.params.headers &&
      Object.keys(this.params).length === 1
    ) {
      extractedHeaders = { ...extractedHeaders, ...this.params.headers };
      this.params = {};
    }

    this.customHeaders = extractedHeaders;
  }

  _isNative() {
    return Capacitor.isNativePlatform();
  }

  _getApiHost() {
    // Jika native app, cek apakah ada override host di tenant config
    if (this._isNative() && currentTenant.apiHost) {
      return currentTenant.apiHost;
    }
    return DEFAULT_API_HOST;
  }

  _getTenantDomain() {
    // Jika native app, gunakan domain dari tenant config
    if (this._isNative()) {
      return currentTenant.domain;
    }
    return window.location.hostname.replace("https://", "");
  }

  async _config() {
    const { params, data, path, method } = this;
    const queryString = this._generateQueryString(params);
    const headers = await this._headers();

    // Gunakan dynamic API host
    const apiHost = this._getApiHost();
    const url = `${apiHost}${path}${queryString}`;

    return {
      method,
      url,
      headers,
      data,
    };
  }

  async _headers() {
    // Get token dari storage yang sesuai (native atau web)
    let token = localStorage.getItem("token");
    if (token && typeof token === "string") {
      token = token.replace(/^"(.*)"$/, "$1").trim();
    }

    // Jika native dan token tidak ada di localStorage, coba ambil dari native storage
    if (this._isNative() && !token) {
      token = await storageService.getItem("token");
      if (token && typeof token === "string") {
        token = token.replace(/^"(.*)"$/, "$1").trim();
      }

      // Jika dapat dari native storage, sync ke localStorage untuk next request
      if (token) {
        localStorage.setItem("token", token);
      } else {
        console.warn('[API] No token found in native storage');
      }
    }

    const headers = {
      Accept: "application/json",
      ...this.customHeaders,
    };

    if (token && !headers.Authorization) {
      headers.Authorization = "Bearer " + token;
    }

    // Clean any empty or invalid Authorization header
    if (headers.Authorization) {
      const authVal = String(headers.Authorization).trim();
      if (
        !authVal ||
        authVal === "Bearer" ||
        authVal === "Bearer null" ||
        authVal === "Bearer undefined" ||
        authVal === "Bearer "
      ) {
        delete headers.Authorization;
      }
    }

    // Tentukan X-Tenant-Domain berdasarkan environment
    if (this._isNative()) {
      // Native app: selalu pakai domain dari tenant config
      headers['X-Tenant-Domain'] = this._getTenantDomain();
    } else {
      const isExtranet = isExtranetHostname();

      if (isExtranet) {
        // Import store dynamically to avoid circular dependency
        const store = require('@/store').default;
        const selectedTenantDomain = store?.state?.extranet?.selectedTenantDomain;

        if (selectedTenantDomain) {
          // Di extranet dengan tenant terpilih: inject target tenant
          headers['X-Tenant-Domain'] = selectedTenantDomain;
        }
        // Jika di extranet tanpa tenant terpilih: TIDAK kirim header (Central DB)
      } else {
        // Di tenant domain biasa: gunakan hostname saat ini
        headers['X-Tenant-Domain'] = this._getTenantDomain();
      }
    }

    return headers;
  }

  _parseToQuery(queryObject, varName = "custom") {
    if (!queryObject) return "";

    if (typeof queryObject !== "object") {
      const trimmed = String(queryObject).trim();
      if (!trimmed) return "";
      return varName !== "custom"
        ? `${varName}=${encodeURIComponent(trimmed)}`
        : encodeURIComponent(trimmed);
    }

    if (Array.isArray(queryObject)) {
      return queryObject
        .filter((val) => val !== undefined && val !== null && val !== "")
        .map((val) => `${varName}[]=${encodeURIComponent(typeof val === "string" ? val.trim() : val)}`)
        .join("&");
    }

    const arrayEntry = Object.entries(queryObject)
      .filter(([, val]) => val !== undefined && val !== null && val !== "")
      .map(([key, val]) => {
        const encodedVal = encodeURIComponent(typeof val === "string" ? val.trim() : val);
        return varName !== "custom"
          ? `${varName}[${encodeURIComponent(key)}]=${encodedVal}`
          : `${encodeURIComponent(key)}=${encodedVal}`;
      });

    return arrayEntry.join("&");
  }

  _generateQueryString() {
    const { params } = this;
    if (!params || typeof params !== "object") return "";

    const arrQuery = [
      this._parseToQuery(params.meta, "meta"),
      this._parseToQuery(params.filter, "filter"),
      this._parseToQuery(params.search, "search"),
      this._parseToQuery(params.sort, "sort"),
      this._parseToQuery(params.custom),
    ];

    const validQueries = arrQuery.filter(Boolean);
    return validQueries.length ? "?" + validQueries.join("&") : "";
  }

  async request() {
    const config = await this._config();
    return new Promise((resolve, reject) => {
      axios(config)
        .then((result) => {
          return resolve(result);
        })
        .catch((result) => {
          // Log untuk debugging 403 errors
          if (result.response?.status === 403) {
            console.error('[API] 403 Forbidden - Check token and permissions');
            console.error('[API] Request config:', {
              url: config.url,
              method: config.method,
              hasToken: !!config.headers.Authorization,
              tenantDomain: config.headers["X-Tenant-Domain"]
            });
          }
          return reject(result);
        })
        .finally(() => { });
    });
  }
}

// const params = {
//     meta:{
//         page:1,
//         limit:1,
//         total:10
//     },
//     filter:{
//         corporate_id:1
//     },
//     custom:{
//         anu:"xxxx"
//     }
// }

export default class {
  static request(requestType = "get", path, params = {}, data = null, options = {}) {
    return new Promise((resolve, reject) => {
      const helperInstance = new axiosHelper(requestType, path, params, data, options);
      helperInstance
        .request()
        .then((result) => {
          return resolve(result);
        })
        .catch((result) => {
          return reject(result);
        })
        .finally(() => { });
    });
  }
}